import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { breadcrumb } from '../../admin/layout/header/header.component';
import { DataService } from '../../services/data.service';
import { MessageService, ConfirmationService, ConfirmEventType } from 'primeng/api';
import { PermissionHandlerService } from '../../services/permission-handler.service';
import { TranslatorPipe } from '../../services/translator.pipe';

interface Customer {
  name: string;
  email: string;
  phone: string;
  organization: string;
  userId: string;
}

interface ProductServiceInfo {
  productId: string;
  productName: string;
  productType?: string;
  unitPrice: number | string; // Allow both number and string (e.g., "not decided yet")
  subtotal: number | string;
  vatAmount?: number | string;
  totalAmount: number | string;
  currency?: string;
}

interface OrderHistory {
  type: 'COMMENT' | 'ATTACHMENT' | 'STATUS_CHANGE';
  createdAt: string;
  createdBy: string;
  comments?: string;
  attachment?: string;
  status?: string;
  _id: string;
}

interface Attachment {
  fileName: string;
  filePath: string;
  fileType: string;
  uploadedAt?: Date;
}

interface Order {
  _id?: string;
  orderId: string;
  customer: Customer;
  productService: ProductServiceInfo;
  orderStatus: 'PENDING' | 'ACCEPTED' | 'UPDATE' | 'REJECTED' | 'CANCELED';
  orderNotes?: string;
  attachments?: Attachment[];
  orderHistory?: OrderHistory[];
  assignedTo?: string;
  isActive?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
  isExpanded?: boolean;
}

@Component({
  selector: 'app-order-listing',
  templateUrl: './order-listing.component.html',
  styleUrls: ['./order-listing.component.scss']
})
export class OrderListingComponent implements OnInit {
  breadcrumbs: breadcrumb[] = [
    {
      name: this.translate('Orders'),
      link: '/orders',
      visible: true
    }
  ];

  ordersData: Order[] = [];
  allOrdersData: Order[] = [];
  spinner: boolean = false;
  totalOrders: number = 0;
  first: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  
  // Dialog state
  showStatusUpdateDialog: boolean = false;
  selectedOrder: Order | null = null;
  statusUpdateNotes: string = '';
  newStatus: 'PENDING' | 'ACCEPTED' | 'UPDATE' | 'REJECTED' | 'CANCELED' = 'PENDING';

  // Filter properties
  searchText: string = '';
  searchById: string = '';
  searchByEmail: string = '';
  selectedStatus: string = 'ALL';
  statusOptions: any[] = [
    { name: this.translate('All Orders'), value: 'ALL' },
    { name: this.translate('Pending in orders'), value: 'PENDING' },
    { name: this.translate('Accepted'), value: 'ACCEPTED' },
    { name: this.translate('Update Required'), value: 'UPDATE' },
    { name: this.translate('Rejected'), value: 'REJECTED' },
    { name: this.translate('Canceled'), value: 'CANCELED' }
  ];

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    public permissionService: PermissionHandlerService
  ) { }

  ngOnInit() {
    this.loadOrders();
  }

  async loadOrders() {
    this.spinner = true;
    
    try {
      // Calculate current page and offset based on pagination
      const currentPage = Math.floor(this.first / this.rows) + 1;
      const offset = this.rows;
      
      // Prepare filters object
      const filters: any = {};
      
      // Add status filter
      if (this.selectedStatus !== 'ALL') {
        filters.orderStatus = this.selectedStatus;
      }
      
      // Add search filters
      if (this.searchById.trim()) {
        filters.orderId = this.searchById.trim();
      }
      
      if (this.searchByEmail.trim()) {
        filters.customerEmail = this.searchByEmail.trim();
      }
      
      if (this.searchText.trim()) {
        filters.searchTerm = this.searchText.trim();
      }
      
      // Use the new POST endpoint with filters
      const response = await this.dataService.postData(`orders/all/${currentPage}/${offset}`, filters);
      
      if (response.error) {
        console.error('Error loading orders:', response.msg);
        this.allOrdersData = [];
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to load orders')
        });
      } else {
        // Transform API data to Order interface format
        if (response.data && response.data.orders && Array.isArray(response.data.orders)) {
          this.allOrdersData = response.data.orders.map((apiData: any) => this.transformOrderData(apiData));
          
          // Update pagination info if available
          if (response.data.pagination) {
            this.totalOrders = response.data.pagination.total || 0;
          }
        } else {
          this.allOrdersData = [];
        }
      }
      
      // Apply local filters (if any additional client-side filtering is needed)
      this.applyFilters();
      
    } catch (error) {
      console.error('Error loading orders:', error);
      this.allOrdersData = [];
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to load orders')
      });
    } finally {
      this.spinner = false;
    }
  }

  transformOrderData(apiData: any): Order {
    return {
      _id: apiData._id,
      orderId: apiData.orderId,
      customer: apiData.customer || {
        name: '',
        email: '',
        phone: '',
        organization: '',
        userId: ''
      },
      productService: apiData.productService || {
        productId: '',
        productName: '',
        unitPrice: 0, // Can be number or string
        subtotal: 0, // Can be number or string
        totalAmount: 0, // Can be number or string
        currency: '	'
      },
      orderStatus: apiData.orderStatus || 'PENDING',
      orderNotes: apiData.orderNotes,
      attachments: apiData.attachments || [],
      orderHistory: apiData.orderHistory || [],
      assignedTo: apiData.assignedTo,
      isActive: apiData.isActive,
      createdBy: apiData.createdBy,
      updatedBy: apiData.updatedBy,
      createdAt: apiData.createdAt ? new Date(apiData.createdAt) : undefined,
      updatedAt: apiData.updatedAt ? new Date(apiData.updatedAt) : undefined,
      isExpanded: false
    };
  }

  applyFilters() {

    this.ordersData = [...this.allOrdersData];
  }

  onSearch() {
    this.first = 0;
    this.currentPage = 1;
    this.loadOrders();
  }

  onStatusFilterChange() {
    this.first = 0;
    this.currentPage = 1;
    this.loadOrders();
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadOrders();
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'ACCEPTED': return 'success';
      case 'UPDATE': return 'warning';
      case 'REJECTED': return 'danger';
      case 'CANCELED': return 'secondary';
      default: return 'info';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'PENDING': return this.translate('Pending in orders');
      case 'ACCEPTED': return this.translate('Accepted');
      case 'UPDATE': return this.translate('Update Required');
      case 'REJECTED': return this.translate('Rejected');
      case 'CANCELED': return this.translate('Canceled');
      case 'UPDATE_SURVEY': return this.translate('Update Survey');
      default: return status;
    }
  }

  getHistoryTypeLabel(type: string): string {
    switch (type) {
      case 'COMMENT': return this.translate('Comment');
      case 'ATTACHMENT': return this.translate('Attachment');
      case 'STATUS_CHANGE': return this.translate('Status Change');
      default: return type;
    }
  }

  getFileNameFromUrl(url: string): string {
    if (!url) return 'Unknown File';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'Unknown File';
  }

  getFileExtension(url: string): string {
    if (!url) return '';
    const fileName = this.getFileNameFromUrl(url);
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return '';
    return fileName.substring(lastDotIndex + 1).toLowerCase();
  }

  getFileIcon(extension: string): string {
    switch (extension) {
      // Document types
      case 'pdf':
        return 'pi-file-pdf';
      case 'doc':
      case 'docx':
        return 'pi-file-word';
      case 'xls':
      case 'xlsx':
        return 'pi-file-excel';
      case 'ppt':
      case 'pptx':
        return 'pi-file-powerpoint';
      case 'txt':
        return 'pi-file-text';
      
      // Image types
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'webp':
        return 'pi-image';
      
      // Archive types
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return 'pi-file-archive';
      
      // Video types
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
      case 'mkv':
        return 'pi-video';
      
      // Audio types
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
      case 'ogg':
        return 'pi-volume-up';
      
      // Code types
      case 'js':
      case 'ts':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return 'pi-code';
      
      // Default
      default:
        return 'pi-file';
    }
  }

  viewOrderDetails(order: Order) {
    this.router.navigate(['/order-details/', order._id]);
  }

  openStatusUpdateDialog(order: Order) {
    this.selectedOrder = order;
    this.newStatus = order.orderStatus;
    this.statusUpdateNotes = '';
    this.showStatusUpdateDialog = true;
  }

  async updateOrderStatus() {
    if (!this.selectedOrder) return;

    try {
      const response = await this.dataService.postData(
        `orders/status/${this.selectedOrder._id}`,
        {
          orderStatus: this.newStatus,
          notes: this.statusUpdateNotes
        }
      );

      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: response.msg || this.translate('Failed to update order status')
        });
      } else {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Order status updated successfully')
        });
        this.showStatusUpdateDialog = false;
        this.loadOrders();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update order status')
      });
    }
  }

  async deleteOrder(order: Order) {
    this.confirmationService.confirm({
      message: this.translate(`Are you sure you want to delete order ${order.orderId}?`),
      header: this.translate('Confirm Delete'),
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const response = await this.dataService.postData(`orders/${order._id}`, {}, 'DELETE');
          
          if (response.error) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate('Error'),
              detail: response.msg || this.translate('Failed to delete order')
            });
          } else {
            this.messageService.add({
              severity: 'success',
              summary: this.translate('Success'),
              detail: this.translate('Order deleted successfully')
            });
            this.loadOrders();
          }
        } catch (error) {
          console.error('Error deleting order:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate('Error'),
            detail: this.translate('Failed to delete order')
          });
        }
      }
    });
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  formatCurrency(amount: number | string, currency: string = 'SAR'): string {
    // If amount is not numeric, return it as-is (text)
    if (typeof amount === 'string' || (typeof amount === 'number' && isNaN(amount))) {
      const strAmount = String(amount);
      // Check if it's a valid numeric string
      const numValue = parseFloat(strAmount);
      if (isNaN(numValue) || !isFinite(numValue) || strAmount.trim() === '') {
        return strAmount; // Return text as-is
      }
      amount = numValue; // Convert to number
    }
    
    // Format numeric amount
    const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
    
    const currencyMap: { [key: string]: string } = {
      'ر.س': 'SAR',
      'ر.س.': 'SAR',
      'SAR': 'SAR'
    };
    
    const isoCurrency = currencyMap[currency] || currency;
    
    if (isoCurrency.length === 3 && /^[A-Z]{3}$/.test(isoCurrency)) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: isoCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      
      if (isoCurrency === 'SAR') {
        return formatted.replace(/SAR|ر\.س\.?|\$|€|£|ر\.س/g, '').trim();
      }
      return formatted;
    } else {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
      return `${formatted} ${currency}`;
    }
  }

  truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  isTextLong(text: string, maxLength: number): boolean {
    return !!(text && text.length > maxLength);
  }

  clearFilters() {
    this.searchText = '';
    this.searchById = '';
    this.searchByEmail = '';
    this.selectedStatus = 'ALL';
    this.first = 0;
    this.currentPage = 1;
    this.loadOrders();
  }

  closeStatusUpdateDialog() {
    this.showStatusUpdateDialog = false;
    this.selectedOrder = null;
    this.statusUpdateNotes = '';
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  toggleOrderExpansion(order: Order) {
    order.isExpanded = !order.isExpanded;
  }

  getCommentCount(order: Order): number {
    if (!order.orderHistory || !Array.isArray(order.orderHistory)) {
      return 0;
    }
    return order.orderHistory.filter(history => history.type === 'COMMENT').length;
  }

  getAttachmentCount(order: Order): number {
    if (order.attachments && Array.isArray(order.attachments)) {
      return order.attachments.length;
    }
    return 0;
  }

  getTotalHistoryCount(order: Order): number {
    if (!order.orderHistory || !Array.isArray(order.orderHistory)) {
      return 0;
    }
    return order.orderHistory.length;
  }

  async copyEmailToClipboard(email: string) {
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate('Warning'),
        detail: this.translate('No email address to copy')
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Email copied to clipboard')
      });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = email;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          this.messageService.add({
            severity: 'success',
            summary: this.translate('Success'),
            detail: this.translate('Email copied to clipboard')
          });
        } else {
          throw new Error('Copy command failed');
        }
      } catch (fallbackErr) {
        document.body.removeChild(textArea);
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Failed to copy email to clipboard')
        });
      }
    }
  }
}
